// AuthContext.tsx
import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {authenticationApi, configApi, i18nTextApi} from "@/boxhub-api";
import PageLoadingSpinner from "@/lib/components/PageLoadingSpinner";
import {clearDataCaches} from "@/lib/datacache";
import {
    CompanyDisplayDTO,
    Language,
    SessionDTO,
    TenantDisplayDTO,
    UserDisplayDTO,
    UserRoleType
} from "@/api/data-contracts";
import packageJson from "../../../package.json";
import {setGlobalDateDisplacement} from "@/lib/date-utils";
import {changeAppLanguage} from "@/app/locale/i18nBundle";
import {resolveAnonymousLanguage} from "@/app/locale/anonymousLanguage";
import {resolveSessionLanguage} from "@/app/locale/sessionLanguage";

/**
 * Resolves the display language for a not-yet-authenticated visitor (business rule: language
 * parameter -> the browser's most-preferred maintained language -> English, restricted to languages
 * with a maintained file — see `resolveAnonymousLanguage`). Loading the available-languages list is itself
 * best-effort: on failure (network, server error) the same rule is applied against the two baseline
 * languages German and English, which are always seeded, so a failed bootstrap call still lands on
 * the correct default instead of hardcoding German.
 */
async function resolveAnonymousLanguageFromEnvironment(): Promise<Language> {
    const langParam = new URLSearchParams(window.location.search).get("lang");
    try {
        const { data: availableLanguages } = await i18nTextApi.getAvailableLanguages();
        return resolveAnonymousLanguage(availableLanguages, langParam, navigator.languages);
    } catch {
        return resolveAnonymousLanguage([Language.GERMAN, Language.ENGLISH], langParam, navigator.languages);
    }
}

/**
 * Resolves the display language for an authenticated user from the session's profile language,
 * restricted to languages with a maintained file (see `resolveSessionLanguage`) — a profile
 * language whose text stock was cleared after the user stored it falls back to German instead of
 * rendering technical keys. Loading the available-languages list is itself best-effort: any
 * failure (network, server error) falls back to `Language.GERMAN` so login never gets stuck on a
 * failed call — unlike the anonymous bootstrap, this fallback stays German unconditionally, since
 * it concerns a returning session's stored profile choice rather than a browser-based guess.
 */
async function resolveSessionLanguageFromEnvironment(sessionLanguage: Language | undefined): Promise<Language> {
    try {
        const { data: availableLanguages } = await i18nTextApi.getAvailableLanguages();
        return resolveSessionLanguage(availableLanguages, sessionLanguage);
    } catch {
        return Language.GERMAN;
    }
}

interface AuthContextType {
    authenticated: boolean;
    user?: UserDisplayDTO;
    roles?: UserRoleType[];
    company?: CompanyDisplayDTO;
    otherCompanyMemberships?: CompanyDisplayDTO[];
    tenant?: TenantDisplayDTO;
    serverVersion?: string;
    clientVersion?: string;
    environment?: string;
    timeDisplacement?: number;
    setTimeDisplacement: (timeDisplacement?: number) => void;
    loading: boolean;
    checkAuth: () => Promise<void>;
    login: (email: string, password: string, recaptchaToken: string | undefined) => Promise<SessionDTO>;
    logout: () => Promise<void>;
    isLoggingOut: boolean;
    switchCompany: (companyId: number) => Promise<SessionDTO>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<SessionDTO>();
    const [loading, setLoading] = useState(true);
    const [envName, setEnvName] = useState<string | undefined>(undefined);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // ---- stable callbacks ----

    const checkAuth = useCallback(async () => {
        setIsLoggingOut(false);
        setLoading(true);
        let language: Language = Language.GERMAN;
        try {
            const response = await authenticationApi.checkAuth();
            setSession(response.data);
            setGlobalDateDisplacement(response.data.displacementDays);
            language = await resolveSessionLanguageFromEnvironment(response.data.language);
        } catch {
            setSession(undefined);
            language = await resolveAnonymousLanguageFromEnvironment();
        } finally {
            await changeAppLanguage(language);
            setLoading(false);
        }
    }, []);

    const login = useCallback(
        async (email: string, password: string, recaptchaToken: string | undefined): Promise<SessionDTO> => {
            setIsLoggingOut(false);
            try {
                const response = await authenticationApi.signIn({ email, password, recaptchaToken });
                setSession(response.data);
                setGlobalDateDisplacement(response.data.displacementDays);
                // Couple the UI to the session's display language right away — otherwise the app keeps
                // rendering the pre-login (anonymous-resolved) language until the next full page load
                // re-runs checkAuth(). No profile-save involved, so this never triggers a 409.
                await changeAppLanguage(await resolveSessionLanguageFromEnvironment(response.data.language));
                // Return the freshly resolved session (not just void) so callers that need the just-logged-in
                // roles immediately (e.g. the post-login feedback-dialog trigger) do not have to rely on a
                // re-render — React has not yet re-rendered AuthProvider at this point in the same microtask
                // continuation, so useAuth().roles here would still be the STALE pre-login value.
                return response.data;
            } catch (error) {
                setSession(undefined);
                throw error;
            }
        },
        []
    );

    const logout = useCallback(async () => {
        setIsLoggingOut(true);
        try {
            await authenticationApi.signOut();
        } finally {
            // Clear the client-side session and reference-data caches even when the server call
            // fails — the cookie may already be invalid server-side, and a failed sign-out must
            // not leave the previous user's data reachable in this tab.
            setSession(undefined);
            clearDataCaches();
        }
    }, []);

    const checkEnv = useCallback(async () => {
        try {
            const response = await configApi.getEnvironmentName();
            setEnvName(response.data);
        } catch (error) {
            console.error("Failed to check environment", error);
            setEnvName(undefined);
        }
    }, []);

    const setTimeDisplacement = useCallback((displacementDays?: number) => {
        setSession((prev) => (prev ? { ...prev, displacementDays } : prev));
    }, []);

    const switchCompany = useCallback(async (companyId: number): Promise<SessionDTO> => {
        const response = await authenticationApi.switchCompany(companyId);
        setSession(response.data);
        setGlobalDateDisplacement(response.data.displacementDays);
        // Permanently mounted areas (header, sidebar) keep rendering across a company switch
        // without a page reload — clear stale reference-data caches (employees, tenant companies)
        // of the previous company so they get reloaded for the new one.
        clearDataCaches();
        return response.data;
    }, []);

    // ---- initialization ----
    useEffect(() => {
        void checkAuth();
        void checkEnv();
    }, [checkAuth, checkEnv]);

    // ---- memoize context value ----
    const value = useMemo<AuthContextType>(
        () => ({
            authenticated: session?.user != null,
            user: session?.user,
            company: session?.company,
            otherCompanyMemberships: session?.otherCompanyMemberships,
            tenant: session?.tenant,
            roles: session?.roles,
            serverVersion: session?.serverVersion,
            environment: envName,
            clientVersion: packageJson.version,
            timeDisplacement: session?.displacementDays,
            setTimeDisplacement,
            loading,
            checkAuth,
            login,
            logout,
            isLoggingOut,
            switchCompany
        }),
        [session, envName, loading, checkAuth, login, logout, setTimeDisplacement, isLoggingOut, switchCompany]
    );

    if (loading) {
        return <PageLoadingSpinner />;
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Returns true if the given roles array contains any client-type role
 * (CLIENT or CLIENT_MANAGING_DIRECTOR).
 *
 * @param roles - Array of user role types from the session
 * @returns true if the user has a client role
 */
export const isClientRole = (roles?: UserRoleType[]): boolean =>
    roles?.includes(UserRoleType.CLIENT) === true || roles?.includes(UserRoleType.CLIENT_MANAGING_DIRECTOR) === true;

/**
 * Returns true if the given roles array contains any operator-type role
 * (OPERATOR, OPERATOR_MANAGING_DIRECTOR, or ADMIN — ADMIN is equivalent to OPERATOR_MANAGING_DIRECTOR).
 *
 * @param roles - Array of user role types from the session
 * @returns true if the user has an operator role
 */
export const isOperatorRole = (roles?: UserRoleType[]): boolean =>
    roles?.includes(UserRoleType.OPERATOR) === true ||
    roles?.includes(UserRoleType.OPERATOR_MANAGING_DIRECTOR) === true ||
    roles?.includes(UserRoleType.ADMIN) === true;

/**
 * Returns true if the given roles array contains any platform admin role
 * (ADMIN or OPERATOR_MANAGING_DIRECTOR).
 * CLIENT_MANAGING_DIRECTOR is intentionally excluded — it is a client-side role,
 * not a platform administrator role.
 *
 * @param roles - Array of user role types from the session
 * @returns true if the user has a platform admin role
 */
export const isAdminRole = (roles?: UserRoleType[]): boolean =>
    roles?.includes(UserRoleType.ADMIN) === true || roles?.includes(UserRoleType.OPERATOR_MANAGING_DIRECTOR) === true;

/**
 * Returns true if the given roles array contains the client managing director role.
 *
 * @param roles - Array of user role types from the session
 * @returns true if the user has CLIENT_MANAGING_DIRECTOR role
 */
export const isClientManagingDirectorRole = (roles?: UserRoleType[]): boolean =>
    roles?.includes(UserRoleType.CLIENT_MANAGING_DIRECTOR) === true;

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
